/**
 * Owner: Masa Bayu
 * File: text-element-to-hit-atom.ts
 * Date:Time: 2026-10-05 15:27
 * Description: Fasa 27 · TextElement → HitTestAtom · linear wordWidths estimate.
 */

import type { HitTestAtom } from '@qx/qx-interaction-engine';
import type { TextElement } from '../extract/spsu-types';

/**
 * Interleaved [word, space, word, space, …, word] matching hit-test linear fallback.
 */
export function estimateWordWidths(text: string, totalWidth: number): number[] {
  const words = text.split(' ');
  if (words.length === 0 || totalWidth <= 0) return [];
  const unit = totalWidth / Math.max(text.length, 1);
  const widths: number[] = [];
  for (let i = 0; i < words.length; i++) {
    widths.push(unit * (words[i]?.length ?? 0));
    if (i < words.length - 1) widths.push(unit);
  }
  return widths;
}

/** Map Face body TextElement → HitTestAtom for Engine 5. */
export function textElementToHitAtom(el: TextElement): HitTestAtom {
  const textContent = el.content;
  return {
    id: el.id,
    type: 'text',
    bbox: {
      x: el.bbox.x,
      y: el.bbox.y,
      width: el.bbox.width,
      height: el.bbox.height,
    },
    textContent,
    wordWidths: estimateWordWidths(textContent, el.bbox.width),
    font: {
      size: el.style.fontSize,
      family: el.style.fontFamily,
      weight: typeof el.style.fontWeight === 'number' ? el.style.fontWeight : 400,
      style: el.style.fontStyle === 'italic' ? 'italic' : 'normal',
    },
  };
}

export function textElementsToHitAtoms(
  elements: readonly TextElement[],
): HitTestAtom[] {
  return elements.map(textElementToHitAtom);
}
